import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { logoutUser, jumpSystem } from '../actions';
import { ServerStatus, checkIsAlive, checkIsAccessible, probeServerAccessible, formatTimestampAge } from '../util/serverStatus';
import { normalizeSearchText } from '../util/searchText';
import ServerStatusModal from '../common/ServerStatusModal';
import AlertModal from '../common/AlertModal';
import './liveStyle.css';

// Status check interval — 30 minutes. With 100+ authServers each tick fires
// per-NVR accessibility probes (localIP then externalIP fallback), so a
// 60s cadence was causing constant background load. The dropdown does NOT
// re-probe on open — opening triggers hundreds of probes which is worse
// than the periodic poll we're trying to slow down. ClickUp 86baduzfv.
const STATUS_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * ServerView - Jump System dropdown
 *
 * Mirrors Java dview's ServerView behavior:
 * - Shows list of authorized servers (fController/eView mode)
 * - Controller server marked with star icon
 * - Offline servers shown in red with crossed-out icon
 * - Firewall-blocked servers shown in goldenrod with shield icon
 * - Online servers shown in normal text with no icon
 * - Version incompatible servers marked with asterisk *
 */

/**
 * Check if server version is compatible with client version
 * Rules from Java dview ServerModel.getListNameAlive():
 * - Major version mismatch: incompatible
 * - Client minor > server minor: incompatible (client too new)
 * - Otherwise: compatible
 */
const checkVersionCompatibility = (clientVersion, serverVersion) => {
    if (!clientVersion || !serverVersion) return true;
    if (clientVersion === '__VERSION__') return true;  // Dev mode
    if (clientVersion === serverVersion) return true;

    const clientParts = clientVersion.split('.');
    const serverParts = serverVersion.split('.');

    if (clientParts.length < 2 || serverParts.length < 2) return true;

    const clientMajor = parseInt(clientParts[0], 10);
    const clientMinor = parseInt(clientParts[1], 10);
    const serverMajor = parseInt(serverParts[0], 10);
    const serverMinor = parseInt(serverParts[1], 10);

    // Major version mismatch
    if (clientMajor !== serverMajor) {
        return false;
    }

    // Client minor > server minor (client is newer)
    if (clientMinor > serverMinor) {
        return false;
    }

    return true;
};

/**
 * Get style for server based on its status
 */
const getServerStyle = (status) => {
    switch (status) {
        case ServerStatus.OFFLINE:
            return { color: '#cc3333' };  // Red for offline
        case ServerStatus.FIREWALL:
            return { color: '#daa520' };  // Goldenrod for firewall
        default:
            return {};  // Default color for online
    }
};

const ServerView = props => {
    // State for server status (map of bSerial -> status)
    const [serverStatuses, setServerStatuses] = useState({});
    const [isChecking, setIsChecking] = useState(false);

    // State for status confirmation modal
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [pendingServer, setPendingServer] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectionError, setConnectionError] = useState(null);

    // State for alert modal
    const [alertModal, setAlertModal] = useState({ show: false, message: '' });

    // Type-ahead filter (David #86bb5dx62) — the eView jump list can be 100+ servers.
    // 200ms debounce, matching CameraTreePanel / the Views search.
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const searchTimer = useRef(null);
    const onSearch = (e) => {
        const v = e.target.value;
        setSearch(v);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => setDebounced(v), 200);
    };
    const clearSearch = () => { if (searchTimer.current) clearTimeout(searchTimer.current); setSearch(''); setDebounced(''); };
    // Clear the pending debounce on unmount so a keystroke <200ms before the Jump sheet
    // closes doesn't fire setDebounced on an unmounted component (mirrors ViewList).
    useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);

    // Refs for cleanup
    const checkIntervalRef = useRef(null);
    const isMountedRef = useRef(true);

    // Check status of all servers
    const checkAllStatuses = useCallback(async (skipAccessibility = false) => {
        if (!props.authServers || props.authServers.length === 0) return;
        if (isChecking) return; // Don't overlap checks

        setIsChecking(true);

        try {
            const newStatuses = {};

            // First pass: quick timestamp-based check
            props.authServers.forEach(server => {
                // Heartbeat-first (#86bb7phhh): a member with a recent heartbeat shows ONLINE immediately;
                // the accessibility pass below only DOWNGRADES to FIREWALL the ones unreachable from here.
                // (Was UNKNOWN on the full check, which flickered the dots grey during the refine.)
                newStatuses[server.bSerial] = checkIsAlive(server.bTimestamp) ? ServerStatus.ONLINE : ServerStatus.OFFLINE;
            });

            // Update with timestamp-based results immediately
            if (isMountedRef.current) {
                setServerStatuses(prev => ({ ...prev, ...newStatuses }));
            }

            // Second pass: refine ALIVE members' DIRECT reachability (ONLINE vs FIREWALL) with BOUNDED
            // concurrency + vantage-ordered probing (#86bb7phhh). Was Promise.all over ALL alive members
            // × BOTH IPs at once — a 100+-member burst on every poll. Now: cap concurrency below the
            // ~6/host limit; probeServerAccessible tries the vantage-likely IP first (skipping the
            // wrong-side 2.5s wait) and still falls back to the other; stop early if the view unmounts.
            if (!skipAccessibility) {
                const aliveServers = props.authServers.filter(s => newStatuses[s.bSerial] !== ServerStatus.OFFLINE);
                const CONCURRENCY = 4;
                const queue = aliveServers.slice();
                const accessibilityStatuses = {};
                const worker = async () => {
                    while (queue.length && isMountedRef.current) {
                        const server = queue.shift();
                        try {
                            const ok = await probeServerAccessible(server);
                            accessibilityStatuses[server.bSerial] = ok ? ServerStatus.ONLINE : ServerStatus.FIREWALL;
                        } catch (e) {
                            accessibilityStatuses[server.bSerial] = ServerStatus.FIREWALL;
                        }
                    }
                };
                await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, () => worker()));

                if (isMountedRef.current) {
                    setServerStatuses(prev => ({ ...prev, ...accessibilityStatuses }));
                }
            }

        } catch (e) {
            console.error('[ServerView] Error checking server statuses:', e);
        } finally {
            if (isMountedRef.current) {
                setIsChecking(false);
            }
        }
    }, [props.authServers, isChecking]);

    // Initial status check and setup periodic checking
    useEffect(() => {
        isMountedRef.current = true;

        // Initial quick check (timestamp only) for immediate feedback
        checkAllStatuses(true);

        // Then do full accessibility check
        const initialCheckTimeout = setTimeout(() => {
            if (isMountedRef.current) {
                checkAllStatuses(false);
            }
        }, 1000);

        // Set up periodic checking
        checkIntervalRef.current = setInterval(() => {
            if (isMountedRef.current) {
                checkAllStatuses(false);
            }
        }, STATUS_CHECK_INTERVAL_MS);

        // Cleanup on unmount
        return () => {
            isMountedRef.current = false;
            clearTimeout(initialCheckTimeout);
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);  // Empty deps - only run on mount/unmount

    // Re-check when authServers changes (i.e., a system jump replaced the list).
    // Only the timestamp-based check runs here — it's local (no network),
    // gives instant dot colors based on the new server's bTimestamps, and
    // doesn't trigger the 134-server accessibility-probe burst that the
    // full check fires off. The 30-minute periodic interval still does
    // the full accessibility sweep when it's actually time to refresh.
    // ClickUp 86baduzfv — per-jump probe storm.
    useEffect(() => {
        if (props.authServers && props.authServers.length > 0) {
            checkAllStatuses(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.authServers]);

    // Jump to a server object (shared by the native <select> and the list-variant rows).
    // Version-incompatible → alert; OFFLINE/FIREWALL → confirm modal; else jump.
    const jumpToServer = (server) => {
        if (!server) return;
        const isCompatible = checkVersionCompatibility(props.sVersion, server.sVersion);
        if (!isCompatible) {
            setAlertModal({ show: true, message: `Server "${server.sName}" has an incompatible version (${server.sVersion}). Please update your software.` });
            return;
        }
        const status = serverStatuses[server.bSerial];
        if (status === ServerStatus.OFFLINE || status === ServerStatus.FIREWALL) {
            setPendingServer(server);
            setShowStatusModal(true);
            return;
        }
        if (props.onJumpStarted) props.onJumpStarted();
        props.jumpSystem(server);
    };
    const initiateServerJump = (e) => {
        if (!e.target.value) return;
        jumpToServer(JSON.parse(e.target.value));
    };

    // Handle modal confirmation - test connectivity then jump
    const handleStatusModalConfirm = async () => {
        if (!pendingServer) return;

        setIsConnecting(true);
        setConnectionError(null);

        try {
            // Try to reach the server using whichIP-style logic
            const { sIP, sLocalIP, bPort } = pendingServer;
            const portSuffix = bPort && bPort !== 80 ? `:${bPort}` : '';

            // Build list of addresses to try
            const attempts = [];
            if (sIP && sIP !== '127.0.0.1') {
                attempts.push(`${sIP}${portSuffix}`);
            }
            if (sLocalIP && sLocalIP !== '127.0.0.1') {
                attempts.push(sLocalIP);
                if (bPort && bPort !== 80) {
                    attempts.push(`${sLocalIP}:${bPort}`);
                }
            }

            let reachable = false;
            for (const addr of attempts) {
                const isAccessible = await checkIsAccessible(addr.split(':')[0], parseInt(addr.split(':')[1]) || 80);
                if (isAccessible) {
                    reachable = true;
                    break;
                }
            }

            if (reachable) {
                // Success - proceed with jump
                setShowStatusModal(false);
                setIsConnecting(false);
                if (props.onJumpStarted) props.onJumpStarted();
                props.jumpSystem(pendingServer);
                setPendingServer(null);
            } else {
                // Failed to connect
                setIsConnecting(false);
                setConnectionError('Unable to reach server');
            }
        } catch (e) {
            setIsConnecting(false);
            setConnectionError('Connection failed');
        }
    };

    // Handle modal cancel
    const handleStatusModalCancel = () => {
        setShowStatusModal(false);
        setPendingServer(null);
        setIsConnecting(false);
        setConnectionError(null);
    };

    // Build display name with version incompatibility marker
    const getDisplayName = (server) => {
        const isCompatible = checkVersionCompatibility(props.sVersion, server.sVersion);
        return `${server.sName}${!isCompatible ? '*' : ''}`;
    };

    // Get tooltip text for server
    const getTooltip = (server) => {
        const status = serverStatuses[server.bSerial];
        const isController = props.useEviewLogin && server.bSerial === props.currentSerial;
        const lastSeen = formatTimestampAge(server.bTimestamp);

        let tooltip = `${server.sName}\n`;
        tooltip += `Version: ${server.sVersion}\n`;
        tooltip += `Last seen: ${lastSeen}\n`;
        tooltip += `IP: ${server.sIP}`;

        if (isController) {
            tooltip += '\n(Controller)';
        }

        switch (status) {
            case ServerStatus.OFFLINE:
                tooltip += '\nStatus: OFFLINE';
                break;
            case ServerStatus.FIREWALL:
                tooltip += '\nStatus: Behind Firewall';
                break;
            case ServerStatus.ONLINE:
                tooltip += '\nStatus: Online';
                break;
            default:
                tooltip += '\nStatus: Checking...';
        }

        return tooltip;
    };

    // Sort servers: controller first, then strict alphabetical. The previous
    // status-tier sort pushed all OFFLINE servers to the bottom — e.g. an
    // offline "O's N. Tucson" appeared below "Villa Rosa" instead of next to
    // the other "O's..." entries. Status is still conveyed via the
    // [OFFLINE]/[FIREWALL] suffix and option color (see getOptionText/Style),
    // but it no longer affects ordering. ClickUp 86baduzfv.
    //
    // localeCompare is called with { sensitivity: 'base', ignorePunctuation: true }
    // so apostrophes, commas, hyphens, etc. don't bias the order (e.g.
    // "O's Plaza Way" sorts with "Oak" not after numeric "0" or punctuation).
    const getSortedServers = () => {
        if (!props.authServers) return [];

        const collatorOpts = { sensitivity: 'base', ignorePunctuation: true };

        return [...props.authServers].sort((a, b) => {
            // Controller always first (eview mode only)
            if (props.useEviewLogin) {
                if (a.bSerial === props.currentSerial) return -1;
                if (b.bSerial === props.currentSerial) return 1;
            }
            return (a.sName || '').localeCompare(b.sName || '', undefined, collatorOpts);
        });
    };

    const sortedServers = getSortedServers();
    // Accent/punctuation-insensitive so "rosas" matches "Rosa's Cafe" (David #86bb5dx62).
    const q = normalizeSearchText(debounced);
    const showSearch = sortedServers.length > 6;
    // Filtered list (excludes the current server + applies the search) — used to render the
    // options + to size the listbox so it shrinks as you type.
    const visibleServers = sortedServers.filter(s => s.bSerial !== props.currentSerial && (!q || normalizeSearchText(s.sName || '').includes(q)));

    // For native <select>/<option> elements, styling is very limited
    // Colors don't work reliably, so we use clear text indicators instead
    const getOptionText = (server) => {
        const name = getDisplayName(server);
        const status = serverStatuses[server.bSerial];
        const isController = props.useEviewLogin && server.bSerial === props.currentSerial;

        let prefix = '';
        let suffix = '';

        if (isController) {
            prefix = '★ ';  // Star prefix for controller
        }

        switch (status) {
            case ServerStatus.OFFLINE:
                suffix = ' [OFFLINE]';
                break;
            case ServerStatus.FIREWALL:
                suffix = ' [FIREWALL]';
                break;
            default:
                break;
        }

        return `${prefix}${name}${suffix}`;
    };

    // Get option style (for browsers that support it)
    const getOptionStyle = (server) => {
        const status = serverStatuses[server.bSerial];
        return getServerStyle(status);
    };

    return (
        <div className="live-section--server">
            {/* Alert Modal for version incompatibility */}
            <AlertModal
                show={alertModal.show}
                title="Version Incompatible"
                message={alertModal.message}
                type="warning"
                eviewMode={props.fEview}
                onClose={() => setAlertModal({ show: false, message: '' })}
            />

            {/* Server Status Confirmation Modal */}
            <ServerStatusModal
                show={showStatusModal}
                serverName={pendingServer?.sName || ''}
                serverStatus={pendingServer ? serverStatuses[pendingServer.bSerial] : null}
                lastSeen={pendingServer ? formatTimestampAge(pendingServer.bTimestamp) : ''}
                isConnecting={isConnecting}
                connectionError={connectionError}
                onCancel={handleStatusModalCancel}
                onConfirm={handleStatusModalConfirm}
            />

            {/* Type-ahead search — the eView jump list can be 100+ servers (#86bb5dx62). */}
            {showSearch && (
                <div className="live-viewlist__search">
                    <span className="live-viewlist__search-ic" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 10-.7.7l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1114 9.5 4.5 4.5 0 019.5 14z" /></svg>
                    </span>
                    <input type="text" className="live-viewlist__search-input" placeholder="Search systems…" value={search} onChange={onSearch} />
                    {search && <button type="button" className="live-viewlist__search-clear" onClick={clearSearch} aria-label="Clear search">×</button>}
                </div>
            )}

            {/* variant="list" (desktop Jump panel, #86bb5dx62): render the systems as a direct
                clickable list — no native <select> that needs a second click. The status dot,
                search, version-check + OFFLINE/FIREWALL confirm all reuse the same logic as the
                dropdown. Default (mobile sheet) keeps the compact native <select>. */}
            {props.variant === 'list' ? (
                <div className="live-jumplist">
                    <div className="live-jumplist__row live-jumplist__row--current" aria-current="true">
                        <span className="live-jumplist__dot live-jumplist__dot--on" />
                        <span className="live-jumplist__name">{props.currentServerName || 'N/A'}{props.useEviewLogin ? ' ★' : ''}</span>
                        <span className="live-jumplist__sub">current</span>
                    </div>
                    {q && visibleServers.length === 0 && (
                        <div className="live-jumplist__empty">No systems match “{search}”</div>
                    )}
                    {visibleServers.map(server => {
                        const status = serverStatuses[server.bSerial];
                        const dotMod = status === ServerStatus.OFFLINE ? '--off' : status === ServerStatus.FIREWALL ? '--fw' : '--on';
                        return (
                            <button
                                key={server.bSerial}
                                type="button"
                                className="live-jumplist__row"
                                onClick={() => jumpToServer(server)}
                                title={getTooltip(server)}
                                style={getOptionStyle(server)}
                            >
                                <span className={'live-jumplist__dot live-jumplist__dot' + dotMod} />
                                <span className="live-jumplist__name">{getDisplayName(server)}</span>
                                <span className="live-jumplist__sub">{server.bSerial}</span>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <>
                    <select
                        value={''}
                        onChange={e => initiateServerJump(e)}
                        className="live-select"
                    >
                        <option value="">Jump System</option>
                        <option value="" disabled>
                            ── Current: {props.currentServerName || 'N/A'} {props.useEviewLogin ? '★' : ''} ──
                        </option>
                        {q && visibleServers.length === 0 && (
                            <option value="" disabled>No systems match “{search}”</option>
                        )}
                        {visibleServers.map(server => (
                            <option
                                key={server.bSerial}
                                value={JSON.stringify(server)}
                                style={getOptionStyle(server)}
                                title={getTooltip(server)}
                            >
                                {getOptionText(server)}
                            </option>
                        ))}
                    </select>
                    <div className="live-status-text" style={{ visibility: isChecking ? 'visible' : 'hidden' }}>
                        Checking server status...
                    </div>
                </>
            )}
        </div>
    );
};

const mapStateToProps = state => {
    const { authServers, sVersion, bSerial, sName } = state.server;
    const { sSess, useEviewLogin } = state.auth;
    return {
        authServers,
        sVersion,
        sSess,
        currentSerial: bSerial,
        currentServerName: sName,
        useEviewLogin
    };
};

export default connect(mapStateToProps, { logoutUser, jumpSystem })(ServerView);
